import { createSignal, onMount } from 'solid-js';
import Text from './Text';
import { FeatureGate } from './FeatureGate';
import PlayerListAttachPicker from './PlayerListAttachPicker';
import PlayerNameEntryList, { type PlayerNameEntry } from './PlayerNameEntryList';
import { useApi, isOk } from '../api';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import type { PlayerListDto, PlayerListSummaryDto } from '../api';
import styles from './PlayerNameEntryList.module.css';

// Players a sellbot hologram leaves out of its stats, either for the whole shop (written at once)
// or for one layout (held in the editor's draft until Save).
export default function HologramExclusionsCard(props: {
    titleKey: string;
    helpKey: string;
    players: readonly PlayerNameEntry[];
    lists: readonly PlayerListSummaryDto[];
    onAddPlayer: (name: string) => void | Promise<void>;
    onRemovePlayer: (player: PlayerNameEntry) => void | Promise<void>;
    onAttachList: (list: PlayerListDto) => void | Promise<void>;
    onDetachList: (playerListId: string) => void | Promise<void>;
}) {
    const api = useApi();
    const playerListEnabled = useFeatureFlag('PLAYER_LIST');
    const [availableLists, setAvailableLists] = createSignal<PlayerListDto[]>([]);

    onMount(async () => {
        if (!playerListEnabled()) return;
        const res = await api.playerList.listPlayerLists();
        if (isOk(res.data)) setAvailableLists(res.data.value.lists ?? []);
    });

    const attach = (playerListId: string) => {
        const list = availableLists().find(l => l.id === playerListId);
        if (list) return props.onAttachList(list);
    };

    return (
        <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
            <h2 class={styles.sectionTitle}><Text val={props.titleKey} /></h2>
            <span class={styles.hint}><Text val={props.helpKey} /></span>
            <FeatureGate flag="PLAYER_LIST">
                <div class={`v ${styles.nestedList}`}>
                    <h3 class={styles.nestedTitle}><Text val="HOLO_EXCLUDE_LISTS" /></h3>
                    <span class={styles.hint}><Text val="HOLO_EXCLUDE_LISTS_HELP" /></span>
                    <PlayerListAttachPicker available={availableLists()} attached={props.lists}
                        onAttach={attach} onDetach={props.onDetachList} />
                </div>
            </FeatureGate>
            <PlayerNameEntryList nested
                titleKey="HOLO_EXCLUDE_NAMES" helpKey="HOLO_EXCLUDE_NAMES_HELP"
                placeholderKey="HOLO_EXCLUDE_PLACEHOLDER" emptyKey="HOLO_EXCLUDE_EMPTY"
                icon="fa-user-slash" items={props.players}
                onAdd={props.onAddPlayer} onRemove={props.onRemovePlayer} />
        </div>
    );
}
